import apiClient from "../../api/api";

/**
 * Retrieves job applications based on optional job/applicant filters and pagination.
 *
 * @async
 * @function requestGetJobApplicants
 * @param {number} [jobId=0] - Job ID used to filter applications. Ignored when 0.
 * @param {number} [applicantId=0] - Applicant ID used to filter applications. Ignored when 0.
 * @param {Object} [filters={}] - Optional filters for the applications list.
 * @param {string} [filters.status] - Application status filter.
 * @param {string} [filters.location] - Applicant city/location filter.
 * @param {Array<string>} [filters.skills] - List of skills to filter by.
 * @param {Array<string>} [filters.languages] - List of languages to filter by.
 * @param {Object} [pagination={ pageNumber: 1, pageSize: 10 }] - Pagination options.
 * @param {number} [pagination.pageNumber=1] - Current page number.
 * @param {number} [pagination.pageSize=10] - Number of records per page.
 * @returns {Promise<Object|Array>} Job application data returned by the API.
 */
export const requestGetJobApplicants = async (
  jobId = 0,
  applicantId = 0,
  filters = {},
  pagination = { pageNumber: 1, pageSize: 10 }
) => {
  const params = {};

  if (jobId > 0) {
    params.jobId = jobId;
  }

  if (applicantId > 0) {
    params.applicantId = applicantId;
  }

  if (filters.search?.trim()) {
    params.search = filters.search.trim();
  }

  if (filters.status) {
    params.status = filters.status;
  }

  if (filters.location) {
    params.city = filters.location;
  }

  if (filters.skills?.length) {
    params.skills = filters.skills.join(", ");
  }

  if (filters.languages?.length) {
    params.languages = filters.languages.join(", ");
  }

  params.page = pagination.pageNumber || 1;
  params.limit = pagination.pageSize || 10;

  const response = await apiClient.get(
    import.meta.env.VITE_GET_JOB_APPLICATIONS_URL,
    { params }
  );

  return response.data;
};

/**
 * Retrieves a talent application resume as a Blob file.
 *
 * @async
 * @function requestGetTalentApplicantionResume
 * @param {number|string} id - Talent application ID.
 * @returns {Promise<Blob>} Resume file returned by the API.
 */
export const requestGetTalentApplicantionResume = async (id) => {
  const params = { id };

  const response = await apiClient.get(
    import.meta.env.VITE_GET_JOB_APPLICATIONS_URL,
    {
      params,
      responseType: "blob",
    }
  );

  return response.data;
};

/**
 * Retrieves an applicant resume as a Blob file.
 *
 * @async
 * @function requestGetApplicantResume
 * @param {number|string} id - Applicant resume ID.
 * @returns {Promise<Blob>} Applicant resume file returned by the API.
 */
export const requestGetApplicantResume = async (id) => {
  const response = await apiClient.get(
    import.meta.env.VITE_GET_APPLICANT_RESUME_URL + `/${id}`,
    { responseType: "blob" }
  );

  return response.data;
};

/**
 * Updates the status of a talent application.
 *
 * @async
 * @function requestUpdateTalentApplicantStatus
 * @param {number|string} id - Talent application ID.
 * @param {string} status - New application status.
 * @returns {Promise<void>} Resolves when the status is successfully updated.
 */
export const requestUpdateTalentApplicantStatus = async (id, status) => {
  const body = { status };

  await apiClient.patch(
    import.meta.env.VITE_GET_JOB_APPLICATIONS_BASE_URL + `/${id}/status`,
    body,
    { successToast: "Talent applicant status successfully updated" }
  );
};

/**
 * Applies an applicant to a specific job.
 *
 * The request sends the applicant introduction video URL, resume file data,
 * and optional application-specific fields according to the job type.
 *
 * For On-site jobs, applicationExtraFields may include:
 * - hasProctoredSatBefore
 * - availableForSatEvent
 * - availableOnsiteByRequiredTime
 * - acceptsUnresponsivePolicy
 * - hasLaptopAndMobileDevice
 *
 * For Remote jobs, applicationExtraFields may include:
 * - experienceLevel
 * - workingSpaceSetup
 *
 * @async
 * @function requestApplyToJob
 * @param {string} jobUuid - Job UUID.
 * @param {number|string} applicantId - Applicant user ID.
 * @param {string} videoUrl - Applicant introduction video URL.
 * @param {string} resumeBase64 - Resume file encoded as Base64.
 * @param {string} fileName - Original resume file name.
 * @param {Object} [applicationExtraFields={}] - Optional extra fields submitted with the application.
 * @param {string|null} [applicationExtraFields.notes] - Optional application notes.
 * @param {string|null} [applicationExtraFields.hasProctoredSatBefore] - Whether the applicant has proctored SAT before.
 * @param {string|null} [applicationExtraFields.availableForSatEvent] - Whether the applicant is available for the SAT event.
 * @param {string|null} [applicationExtraFields.availableOnsiteByRequiredTime] - Whether the applicant can be onsite by the required time.
 * @param {string|null} [applicationExtraFields.acceptsUnresponsivePolicy] - Whether the applicant accepts the unresponsive communication policy.
 * @param {string|null} [applicationExtraFields.hasLaptopAndMobileDevice] - Whether the applicant has both laptop and mobile device.
 * @param {string|null} [applicationExtraFields.experienceLevel] - Applicant experience level for remote roles.
 * @param {string|null} [applicationExtraFields.workingSpaceSetup] - Comma-separated list of available equipment/resources.
 * @returns {Promise<void>} Resolves when the application is successfully submitted.
 */
export const requestApplyToJob = async (jobUuid, applicantId, videoUrl, resumeBase64, fileName, applicationExtraFields = {} ) => {
  const data = {
    videoUrl: videoUrl,
    resume: {
      fileBase64: resumeBase64,
      fileName: fileName,
      contentType: "application/pdf",
    },
    ...applicationExtraFields,
  };

  await apiClient.post(
    `${import.meta.env.VITE_GET_JOB_APPLICATIONS_BASE_URL}/${jobUuid}/apply/${applicantId}`,
    data,
    { successToast: "Job application submitted successfully" }
  );
};

/**
 * Opens or creates the application pipeline for a specific job.
 *
 * @async
 * @function requestJobApplicationPipeline
 * @param {number|string} jobId - Job ID.
 * @returns {Promise<Object>} Pipeline data returned by the API.
 */
export const requestJobApplicationPipeline = async (jobId) => {
  const response = await apiClient.patch(
    `${import.meta.env.VITE_GET_JOB_APPLICATIONS_BASE_URL}/${jobId}/open-pipeline`
  );

  return response.data;
};

/**
 * Retrieves the external applicant ticket URL for a specific job application.
 *
 * @async
 * @function getApplicantTicketUrl
 * @param {number|string} jobId - Job ID.
 * @param {number|string} applicantId - Applicant ID.
 * @returns {Promise<Object|string>} Ticket URL data returned by the API.
 */
export const getApplicantTicketUrl = async (jobId, applicantId) => {
  const params = { jobId, applicantId };

  const response = await apiClient.get("job-applications/applications", {
    params,
  });

  return response.data;
};

export const requestUpdateTalentApplicantNote = async(applicantionId, notes) => {
  const data ={
    notes
  }

  const response = await apiClient.patch(`${import.meta.env.VITE_GET_JOB_APPLICATIONS_BASE_URL}/${applicantionId}/notes`, data, {successToast: "Note updated successfully!"})

  return response.data
}